import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal, Alert } from 'react-bootstrap';
import { IoInformationCircleOutline } from 'react-icons/io5';
import PunchPointsModal from './PunchPointsModal';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const APFCPanelForm = ({ role = cookie.get('user-role'), userId = cookie.get('user-id') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [pendingRework, setPendingRework] = useState(null);
  const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',
      equipment_manufacturer: '',
      serial_number: '',
      equipment_tag: '',
      rated_output: '',
      visual_checks: Array(14).fill({ status: '', remark: '' }),
      physical_checks: Array(6).fill({ status: '', remark: '' }),
      l1_other_comments: '',
      l1_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l1_status: 'draft',
      db_panel_healthy: '',
      functional_checks_status: '',
      testing_commissioning: '',
      insulation_resistance_test: '',
      capacitor_values_check: '',
      performance_records_check: '',
      l2_office_comments: '',
      functional_checks: Array(5).fill({ status: '', remark: '' }),
      test_instruments: [
        { description: 'IR Tester', serial_no: '', range: '', calibration_status: '' },
        { description: 'Digital Multi meter / Tong Tester', serial_no: '', range: '', calibration_status: '' },
      ],
      insulation_tests: Array(10).fill({ value: '', remarks: '' }),
      capacitor_values: Array(6).fill({ rating: '', value: '', remarks: '' }),
      performance_records: Array(6).fill({
        disconnect_meter: '',
        main_lt_panel: '',
        sub_feeder: '',
        apfc_panel: '',
        remarks: '',
      }),
      observations: { a: '', b: '', c: '' },
      l2_other_comments: '',
      l2_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l2_status: 'draft',
      manager_approve: false,
    },
  });

  const formValues = useWatch({ control });
  const l1_status = useWatch({ control, name: 'l1_status' });
  const l2_status = useWatch({ control, name: 'l2_status' });
  const l1_manager_id = useWatch({ control, name: 'l1_manager_id' });
  const l2_manager_id = useWatch({ control, name: 'l2_manager_id' });
  const punch_points = useWatch({ control, name: 'punch_points' });

  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const l1_status = data.l1_status;
      const l2_status = data.l2_status;
      let common_status = 'draft';
      if (l1_status === 'CONTRACTOR' && l2_status === 'CONTRACTOR') {
        common_status = 'CONTRACTOR';
      } else if (l1_status === 'MANAGER' && l2_status === 'MANAGER') {
        common_status = 'MANAGER';
      } else if (l1_status === 'QUALITY' && l2_status === 'QUALITY') {
        common_status = 'QUALITY';
      } else if (l1_status === 'approved' && l2_status === 'approved') {
        common_status = 'approved';
      }

      const payload = {
        form_data: {
          status: common_status,
          form_id: formId,
          formName: 'apfc_panel',
          station_id: data.station_id,
          project_name: data.project_name,
          equipment_manufacturer: data.equipment_manufacturer,
          serial_number: data.serial_number,
          equipment_tag: data.equipment_tag,
          rated_output: data.rated_output,
          manager_approve: data.manager_approve,
          l1: {
            visual_checks: data.visual_checks,
            physical_checks: data.physical_checks,
            other_comments: data.l1_other_comments,
            comments: data.l1_comments,
            status: l1_status,
          },
          l2: {
            db_panel_healthy: data.db_panel_healthy,
            functional_checks_status: data.functional_checks_status,
            testing_commissioning: data.testing_commissioning,
            insulation_resistance_test: data.insulation_resistance_test,
            capacitor_values_check: data.capacitor_values_check,
            performance_records_check: data.performance_records_check,
            office_comments: data.l2_office_comments,
            functional_checks: data.functional_checks,
            test_instruments: data.test_instruments,
            insulation_tests: data.insulation_tests,
            capacitor_values: data.capacitor_values,
            performance_records: data.performance_records,
            observations: data.observations,
            other_comments: data.l2_other_comments,
            comments: data.l2_comments,
            status: l2_status,
          },
          punch_points: data.punch_points,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => {
    autoSave(formValues);
  }, [formValues, autoSave]);

  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'apfc_panel' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const data = viewFormData || {};
      setValue('station_id', data.station_id || stationId || '');
      setValue('project_name', data.project_name || '');
      setValue('equipment_manufacturer', data.equipment_manufacturer || '');
      setValue('serial_number', data.serial_number || '');
      setValue('equipment_tag', data.equipment_tag || '');
      setValue('rated_output', data.rated_output || '');
      setValue('visual_checks', Array.isArray(data.l1?.visual_checks)
        ? data.l1.visual_checks
        : Array(14).fill({ status: '', remark: '' }));
      setValue('physical_checks', Array.isArray(data.l1?.physical_checks)
        ? data.l1.physical_checks
        : Array(6).fill({ status: '', remark: '' }));
      setValue('l1_other_comments', data.l1?.other_comments || '');
      setValue('l1_comments', data.l1?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l1_status', data.l1?.status || 'draft');
      setValue('db_panel_healthy', data.l2?.db_panel_healthy || '');
      setValue('functional_checks_status', data.l2?.functional_checks_status || '');
      setValue('testing_commissioning', data.l2?.testing_commissioning || '');
      setValue('insulation_resistance_test', data.l2?.insulation_resistance_test || '');
      setValue('capacitor_values_check', data.l2?.capacitor_values_check || '');
      setValue('performance_records_check', data.l2?.performance_records_check || '');
      setValue('l2_office_comments', data.l2?.office_comments || '');
      setValue('functional_checks', Array.isArray(data.l2?.functional_checks)
        ? data.l2.functional_checks
        : Array(5).fill({ status: '', remark: '' }));
      setValue('test_instruments', Array.isArray(data.l2?.test_instruments)
        ? data.l2.test_instruments
        : [
            { description: 'IR Tester', serial_no: '', range: '', calibration_status: '' },
            { description: 'Digital Multi meter / Tong Tester', serial_no: '', range: '', calibration_status: '' },
          ]);
      setValue('insulation_tests', Array.isArray(data.l2?.insulation_tests)
        ? data.l2.insulation_tests
        : Array(10).fill({ value: '', remarks: '' }));
      setValue('capacitor_values', Array.isArray(data.l2?.capacitor_values)
        ? data.l2.capacitor_values
        : Array(6).fill({ rating: '', value: '', remarks: '' }));
      setValue('performance_records', Array.isArray(data.l2?.performance_records)
        ? data.l2.performance_records
        : Array(6).fill({ disconnect_meter: '', main_lt_panel: '', sub_feeder: '', apfc_panel: '', remarks: '' }));
      setValue('observations', data.l2?.observations || { a: '', b: '', c: '' });
      setValue('l2_other_comments', data.l2?.other_comments || '');
      setValue('l2_comments', data.l2?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l2_status', data.l2?.status || 'draft');
      setValue('manager_approve', data.manager_approve || false);
      setValue('l1_manager_id', data.l1?.managerId || null);
      setValue('l2_manager_id', data.l2?.managerId || null);
      setValue('punch_points', data.punch_points || '');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue, role]);

  const handleImageUpload = (e, level, sigRole) => {
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB limit
    if (file) {
      if (file.size > maxSizeInBytes) {
        alert('File size exceeds 50kb. Please upload a smaller image.');
        handleResetSignature(level, sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(`${level}_comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (level, sigRole, type) => {
    setValue(`${level}_comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ level, role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (level, sigRole) => {
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`${currentSignature.level}_comments.${currentSignature.role}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const onSubmit = (level, status) => (data) => {
    if (role === 'engineer' && getValues(`${level}_status`) !== 'draft' && getValues(`${level}_status`) !== 'CONTRACTOR') return;
    if (role === 'contractor' && getValues(`${level}_status`) !== 'draft' && getValues(`${level}_status`) !== 'CONTRACTOR') return;

    let l1_status = data.l1_status;
    let l2_status = data.l2_status;
    let common_status = 'draft';
    let manager_approve = data.manager_approve;

    if (level === 'l1') {
      l1_status = status;
    } else if (level === 'l2') {
      l2_status = status;
    }

    if (role === 'engineer') {
      status = 'CONTRACTOR';
      if (level === 'l1') l1_status = 'CONTRACTOR';
      if (level === 'l2') l2_status = 'CONTRACTOR';
    } else if (role === 'contractor') {
      status = 'MANAGER';
      if (level === 'l1') l1_status = 'MANAGER';
      if (level === 'l2') l2_status = 'MANAGER';
    } else if (role === 'manager' && status === 'approved') {
      status = 'QUALITY';
      if (level === 'l1') l1_status = 'QUALITY';
      if (level === 'l2') l2_status = 'QUALITY';
      manager_approve = true;
    } else if (role === 'manager' && status === 'draft') {
      if (level === 'l1') l1_status = 'draft';
      if (level === 'l2') l2_status = 'draft';
    } else if (role === 'quality' && status === 'approved') {
      if (level === 'l1') l1_status = 'approved';
      if (level === 'l2') l2_status = 'approved';
    } else if (role === 'quality' && status === 'draft') {
      if (level === 'l1') l1_status = 'draft';
      if (level === 'l2') l2_status = 'draft';
    }

    if (l1_status === 'CONTRACTOR' && l2_status === 'CONTRACTOR') {
      common_status = 'CONTRACTOR';
    } else if (l1_status === 'MANAGER' && l2_status === 'MANAGER') {
      common_status = 'MANAGER';
    } else if (l1_status === 'QUALITY' && l2_status === 'QUALITY') {
      common_status = 'QUALITY';
    } else if (l1_status === 'approved' && l2_status === 'approved') {
      common_status = 'approved';
    }

    const payload = {
      form_data: {
        status: common_status,
        form_id: formId,
        formName: 'apfc_panel',
        station_id: data.station_id,
        project_name: data.project_name,
        equipment_manufacturer: data.equipment_manufacturer,
        serial_number: data.serial_number,
        equipment_tag: data.equipment_tag,
        rated_output: data.rated_output,
        manager_approve: manager_approve,
        l1: {
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          other_comments: data.l1_other_comments,
          comments: data.l1_comments,
          status: l1_status,
        },
        l2: {
          db_panel_healthy: data.db_panel_healthy,
          functional_checks_status: data.functional_checks_status,
          testing_commissioning: data.testing_commissioning,
          insulation_resistance_test: data.insulation_resistance_test,
          capacitor_values_check: data.capacitor_values_check,
          performance_records_check: data.performance_records_check,
          office_comments: data.l2_office_comments,
          functional_checks: data.functional_checks,
          test_instruments: data.test_instruments,
          insulation_tests: data.insulation_tests,
          capacitor_values: data.capacitor_values,
          performance_records: data.performance_records,
          observations: data.observations,
          other_comments: data.l2_other_comments,
          comments: data.l2_comments,
          status: l2_status,
        },
        punch_points: data.punch_points,
      },
    };
    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    toast.success(`Level ${level.toUpperCase()} ${status === 'pending' || status === 'approved' ? 'Submitted' : 'Saved as Draft'} Successfully`);
    navigate(-1);
  };

  const renderSignatureBlock = (level, signatureRole, field) => {
    const levelStatus = getValues(`${level}_status`);
    const managerAssigned = level === 'l1' ? getValues('l1_manager_id') : getValues('l2_manager_id');
    const isEditableRole = (role === 'quality' || !managerAssigned || managerAssigned === userId) &&
      ((role === 'engineer' && signatureRole === 'charge_engineer' && (levelStatus === 'draft' || levelStatus === 'CONTRACTOR' || levelStatus === 'MANAGER')) ||
      (role === 'contractor' && signatureRole === 'contractor' && (levelStatus === 'draft' || levelStatus === 'CONTRACTOR')) ||
      (role === 'quality' && signatureRole === 'quality' && levelStatus === 'QUALITY'));

    if (field === 'Date') {
      return (
        <Form.Control
          type="date"
          {...register(`${level}_comments.${signatureRole}.date`)}
          disabled={!isEditableRole}
        />
      );
    } else if (field === 'Signature') {
      return (
        <div>
          <Form.Select
            {...register(`${level}_comments.${signatureRole}.signatureType`)}
            onChange={(e) => handleSignatureTypeChange(level, signatureRole, e.target.value)}
            className="mb-2"
            disabled={!isEditableRole}
          >
            <option value="upload">Image Upload</option>
            <option value="digital">Digital Sign</option>
          </Form.Select>
          {_.get(formValues, `${level}_comments.${signatureRole}.signatureType`) === 'upload' && (
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, level, signatureRole)}
              className="mb-2"
              disabled={!isEditableRole}
            />
          )}
          {_.get(formValues, `${level}_comments.${signatureRole}.signature`) && (
            <div>
              <img
                src={_.get(formValues, `${level}_comments.${signatureRole}.signature`)}
                alt="Signature"
                style={{ maxWidth: '100px', marginTop: '5px' }}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleResetSignature(level, signatureRole)}
                className="mt-2"
                disabled={!isEditableRole}
              >
                Reset Signature
              </Button>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <Form.Control
          type="text"
          {...register(`${level}_comments.${signatureRole}.${field.toLowerCase()}`)}
          disabled={!isEditableRole}
        />
      );
    }
  };

  const disableLevel1Tab = role === 'contractor' && getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR';
  const disableLevel2Tab = role === 'contractor' && getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR';
  const isLevel1Editable = (!l1_manager_id || l1_manager_id === userId) && ((role === 'engineer' && (l1_status === 'draft' || l1_status === 'CONTRACTOR' || l1_status === 'MANAGER')) || (role === 'contractor' && (l1_status === 'draft' || l1_status === 'CONTRACTOR')));
  const isLevel2Editable = (!l2_manager_id || l2_manager_id === userId) && ((role === 'engineer' && (l2_status === 'draft' || l2_status === 'CONTRACTOR' || l2_status === 'MANAGER')) || (role === 'contractor' && (l2_status === 'draft' || l2_status === 'CONTRACTOR')));
  const canCommentLevel1 = (role === 'engineer' && (getValues('l1_status') === 'draft' || getValues('l1_status') === 'CONTRACTOR')) || (role === 'contractor' && getValues('l1_status') === 'CONTRACTOR') || (role === 'manager' && getValues('l1_status') === 'MANAGER') || (role === 'quality' && getValues('l1_status') === 'QUALITY');
  const canCommentLevel2 = (role === 'engineer' && (getValues('l2_status') === 'draft' || getValues('l2_status') === 'CONTRACTOR')) || (role === 'contractor' && getValues('l2_status') === 'CONTRACTOR') || (role === 'manager' && getValues('l2_status') === 'MANAGER') || (role === 'quality' && getValues('l2_status') === 'QUALITY');

  const canEditPunchPoints = ['manager', 'quality', 'maintenance', 'operation'].includes((role || '').toLowerCase());
  const handleReworkClick = (level, action) => (e) => {
    if (e) e.preventDefault();
    setPendingRework({ level, action });
    setShowPunchModal(true);
  };

  const handleSavePunchPoints = async () => {
    if (pendingRework) {
      setShowPunchModal(false);
      handleSubmit(onSubmit(pendingRework.level, pendingRework.action))();
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-body container-xl">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title mb-0">APFC Panel - Site Acceptance Testing</h2>
        <Button variant="outline-warning" size="sm" onClick={() => setShowPunchModal(true)}>
          💬 See Comments
        </Button>
      </div>
      {(!isLevel1Editable && !isLevel2Editable && role === 'engineer' && l1_status !== 'draft' && l1_status !== 'CONTRACTOR' && l2_status !== 'draft' && l2_status !== 'CONTRACTOR') && (
        <div className="alert alert-warning mb-4">
          This form is in View Mode Only for Engineer.
        </div>
      )}
      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Manufacturer</Form.Label>
              <Form.Control type="text" {...register('equipment_manufacturer')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Serial Number</Form.Label>
              <Form.Control type="text" {...register('serial_number')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Tag</Form.Label>
              <Form.Control type="text" {...register('equipment_tag')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rated Output</Form.Label>
              <Form.Control type="text" {...register('rated_output')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
          </Col>
        </Row>

        {punch_points && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between shadow-sm border-0 mb-4" style={{ borderRadius: '12px', background: '#fff9db', color: '#856404' }}>
            <div className="d-flex align-items-center">
              <IoInformationCircleOutline className="me-2 text-warning" size={24} />
              <div>
                <strong className="d-block">Rework Comments Available</strong>
                <span className="small">Please review the comments from the manager/quality team.</span>
              </div>
            </div>
            <Button 
              variant="warning" 
              size="sm" 
              className="rounded-pill px-3 fw-bold shadow-sm"
              onClick={() => setShowPunchModal(true)}
            >
              View Comments
            </Button>
          </Alert>
        )}

        <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
          <Tab eventKey="level1" title="Level 1 - Pre-Commissioning" disabled={disableLevel1Tab}>
            <h3 className="mt-4">2. Visual Checks</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
              </thead>
              <tbody>
                {[
                  'Is the identification of equipment tags as per the Single Line Diagram (SLD)?',
                  'Is the manufacturer’s nameplate identified as per the Guaranteed Technical Parameters (GTP)?',
                  'Has the APFC panel vendor test certificate (TC) been verified?',
                  'Are gaskets present on the panel doors?',
                  'Has a visual inspection of lamps and indicators been conducted?',
                  'Is the panel properly earthed with double earthing?',
                  'Are all live parts covered with acrylic, polycarbonate, or glass sheets to prevent exposure?',
                  'Is proper ventilation, such as louvers, provided?',
                  'Has the panel been inspected for physical damage and proper door locking arrangements?',
                  'What is the type of installation: Separate panel or inside the PSS LT compartment?',
                  'Has the power cable termination been properly tightened, with appropriate markings on bolts or the busbar?',
                  'Has the internal assembly been checked for any damage?',
                  'If any missing parts as per the SLD/GTP?',
                  'Is the location and orientation of the panel as per the design and drawing?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="Yes"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isLevel1Editable}
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="No"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isLevel1Editable}
                      />
                      <Form.Check
                        inline
                        label="NA"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="NA"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isLevel1Editable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`visual_checks.${index}.remark`)}
                        disabled={!isLevel1Editable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">3. Physical Checks</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
              </thead>
              <tbody>
                {[
                  'Has a physical check been conducted to ensure no loose control wiring and proper cable dressing as per SOP?',
                  'Have the internal components been installed as per the approved drawing?',
                  'Are all cable glands properly tightened?',
                  'Is the panel board securely tightened to the base frame?',
                  'Is the earth strip/cable properly tightened with the panel?',
                  'Is the automatic/manual operation functioning correctly?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="Yes"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isLevel1Editable}
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="No"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isLevel1Editable}
                      />
                      <Form.Check
                        inline
                        label="NA"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="NA"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isLevel1Editable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`physical_checks.${index}.remark`)}
                        disabled={!isLevel1Editable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">4. Other Comments / Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l1_other_comments')}
                disabled={!canCommentLevel1}
              />
            </Form.Group>

            <h3 className="mt-4">5. Level 1 Pre-Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((signatureRole) => (
                      <td key={signatureRole}>
                        {renderSignatureBlock('l1', signatureRole, field)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            {role === 'manager' && getValues('l1_status') === 'MANAGER' ? (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l1', 'approved'))}
                  className="me-2"
                >
                  Approve Level 1
                </Button>
                <Button
                  variant="danger"
                  onClick={handleReworkClick('l1', 'draft')}
                >
                  Re-Work Level 1
                </Button>
              </div>
            ) : role === 'quality' && getValues('l1_status') === 'QUALITY' ? (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l1', 'approved'))}
                  className="me-2"
                >
                  Approve Level 1
                </Button>
                <Button
                  variant="danger"
                  onClick={handleReworkClick('l1', 'draft')}
                >
                  Apply for Resubmit Level 1
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l1', 'pending'))}
                  disabled={(role === 'engineer' || role === 'contractor') ? (getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR') : true}
                >
                  Submit Level 1
                </Button>
                <Button
                  variant="secondary"
                  style={{ marginLeft: '10px' }}
                  onClick={handleSubmit(onSubmit('l1', 'draft'))}
                  disabled={(role === 'engineer' || role === 'contractor') ? (getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR') : true}
                >
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>

          <Tab eventKey="level2" title="Level 2 - Commissioning" disabled={disableLevel2Tab}>
            <div className="alert alert-info mt-4">
              Note: Ensure all below points are in co-ordinate with ANNEXURE: APFC PANEL: COMMISSIONING PART: Level 2 and if all tests are found correct select Yes / No.
            </div>

            <h3 className="mt-4">5. Commissioning Part</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>APFC Panel Found Healthy & Ready to Work with System</Form.Label>
                  <Form.Select {...register('db_panel_healthy')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Functional Checks</Form.Label>
                  <Form.Select {...register('functional_checks_status')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Testing & Commissioning</Form.Label>
                  <Form.Select {...register('testing_commissioning')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Insulation Resistance Test</Form.Label>
                  <Form.Select {...register('insulation_resistance_test')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Capacitor Values Check</Form.Label>
                  <Form.Select {...register('capacitor_values_check')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>APFC Panel Performance Records</Form.Label>
                  <Form.Select {...register('performance_records_check')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h3 className="mt-4">6. Level 2 Commissioning Comments For Office</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l2_office_comments')}
                disabled={!canCommentLevel2}
              />
            </Form.Group>

            <h3 className="mt-4">7. Functional Checks</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
              </thead>
              <tbody>
                {[
                  'Protection Check – Automatic & Manual',
                  'Trial Tripping – MCCB, ELR, Contactor Tripping Test',
                  'Annunciation – If any',
                  'Alarms Trials – If any',
                  'All pilot lamps glow check',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        name={`functional_checks_${index}`}
                        value="Yes"
                        {...register(`functional_checks.${index}.status`)}
                        disabled={!isLevel2Editable}
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        name={`functional_checks_${index}`}
                        value="No"
                        {...register(`functional_checks.${index}.status`)}
                        disabled={!isLevel2Editable}
                      />
                      <Form.Check
                        inline
                        label="NA"
                        type="radio"
                        name={`functional_checks_${index}`}
                        value="NA"
                        {...register(`functional_checks.${index}.status`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`functional_checks.${index}.remark`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">8. Test Instruments Details and Calibration Status</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Instrument Description</th><th>Instrument Serial No.</th><th>Range</th><th>Calibration Status</th></tr>
              </thead>
              <tbody>
                {[
                  'IR Tester',
                  'Digital Multi meter / Tong Tester',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`test_instruments.${index}.serial_no`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`test_instruments.${index}.range`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`test_instruments.${index}.calibration_status`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">9. Testing & Commissioning (Electrical Checks / Tests)</h3>
            <h4>I) Insulation Resistance Test (Minimum value - 2 MΩ)</h4>
            <div className="alert alert-info">
              Note: During Megger testing isolate neutral link, remove Capacitor Bank, space heater fuses if any.
            </div>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Phase to Phase</th><th>Value (MΩ/GΩ)</th><th>Remarks</th></tr>
              </thead>
              <tbody>
                {[
                  'R - Y',
                  'Y - B',
                  'B - R',
                  'R - N',
                  'Y - N',
                  'B - N',
                  'R - E',
                  'Y - E',
                  'B - E',
                  'N - E',
                ].map((phase, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{phase}</td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`insulation_tests.${index}.value`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`insulation_tests.${index}.remarks`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h4>II) Capacitor Values Check (As per value of Capacitor)</h4>
            <div className="alert alert-info">
              Note: Calibration Test Certificate submitted at the time of testing.
            </div>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Capacitor Rating</th><th>Measured Value (µF)</th><th>Remarks (Pass/Fail)</th></tr>
              </thead>
              <tbody>
                {Array(6).fill().map((_, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`capacitor_values.${index}.rating`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`capacitor_values.${index}.value`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`capacitor_values.${index}.remarks`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h4>III) APFC Panel Performance Records</h4>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Conditional Test</th>
                  <th>Power Factor Values</th>
                  <th>Reference</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { condition: 'APFC Disconnected Mode: No Load', reference: '> 0.95 & < 1' },
                  { condition: 'APFC Disconnected Mode: 50% Load', reference: '> 0.95 & < 1' },
                  { condition: 'APFC Disconnected Mode: 100% Load', reference: '> 0.95 & < 1' },
                  { condition: 'APFC Connected Mode: No Load', reference: '> 0.95 & < 1' },
                  { condition: 'APFC Connected Mode: 50% Load', reference: '> 0.95 & < 1' },
                  { condition: 'APFC Connected Mode: 100% Load', reference: '> 0.95 & < 1' },
                ].map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.condition}</td>
                    <td>
                      <Row>
                        <Col>
                          <Form.Control
                            type="text"
                            placeholder="Disconnect Meter"
                            {...register(`performance_records.${index}.disconnect_meter`)}
                            disabled={!isLevel2Editable}
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            type="text"
                            placeholder="Main LT Panel Feeder"
                            {...register(`performance_records.${index}.main_lt_panel`)}
                            disabled={!isLevel2Editable}
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            type="text"
                            placeholder="LT Panel Sub-feeder"
                            {...register(`performance_records.${index}.sub_feeder`)}
                            disabled={!isLevel2Editable}
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            type="text"
                            placeholder="APFC Panel"
                            {...register(`performance_records.${index}.apfc_panel`)}
                            disabled={!isLevel2Editable}
                          />
                        </Col>
                      </Row>
                    </td>
                    <td>{item.reference}</td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`performance_records.${index}.remarks`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">10. Observations during Commissioning / Testing (If Any)</h3>
            {['a', 'b', 'c'].map((key) => (
              <Form.Group className="mb-3" key={key}>
                <Form.Label>{key.toUpperCase()}.</Form.Label>
                <Form.Control
                  type="text"
                  {...register(`observations.${key}`)}
                  disabled={!isLevel2Editable}
                />
              </Form.Group>
            ))}

            <h3 className="mt-4">11. Other Comments / Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l2_other_comments')}
                disabled={!canCommentLevel2}
              />
            </Form.Group>

            <h3 className="mt-4">12. Level 2 Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((signatureRole) => (
                      <td key={signatureRole}>
                        {renderSignatureBlock('l2', signatureRole, field)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            {role === 'manager' && getValues('l2_status') === 'MANAGER' ? (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l2', 'approved'))}
                  className="me-2"
                >
                  Approve Level 2
                </Button>
                <Button
                  variant="danger"
                  onClick={handleReworkClick('l2', 'draft')}
                >
                  Re-Work Level 2
                </Button>
              </div>
            ) : role === 'quality' && getValues('l2_status') === 'QUALITY' ? (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l2', 'approved'))}
                  className="me-2"
                >
                  Approve Level 2
                </Button>
                <Button
                  variant="danger"
                  onClick={handleReworkClick('l2', 'draft')}
                >
                  Apply for Resubmit Level 2
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l2', 'pending'))}
                  disabled={(role === 'engineer' || role === 'contractor') ? (getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR') : true}
                >
                  Submit Level 2
                </Button>
                <Button
                  variant="secondary"
                  style={{ marginLeft: '10px' }}
                  onClick={handleSubmit(onSubmit('l2', 'draft'))}
                  disabled={(role === 'engineer' || role === 'contractor') ? (getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR') : true}
                >
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>
        </Tabs>

        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2">
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
      <PunchPointsModal
        show={showPunchModal}
        onHide={() => { setShowPunchModal(false); setPendingRework(null); }}
        value={punch_points}
        onChange={(val) => setValue('punch_points', val)}
        onSave={handleSavePunchPoints}
        canEdit={canEditPunchPoints && !!pendingRework}
      />
    </div>
  );
};

// PropTypes validation for the 'role' prop
APFCPanelForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
  userId: PropTypes.string,
};

export default APFCPanelForm;
